import { resolveStatus } from './StatusPicker.jsx'

/**
 * QuarterlyPrereadView — render the structured Quarterly Pre-read.
 *
 * Shape: { topLine, goalHealth: [{pillarName, status, summary}],
 *          highlights: [string], needsAttention: [string] }
 *
 * Used both on the live preview pane and the archive-view replay. The
 * "Copy as markdown" affordance is handled in the parent — this view
 * is rendering only.
 */
export default function QuarterlyPrereadView({ preread }) {
  if (!preread) return null
  const { topLine, goalHealth, highlights, needsAttention } = preread

  return (
    <div
      style={{
        fontSize: 13,
        lineHeight: 1.6,
        color: 'var(--text)',
      }}
    >
      {topLine && (
        <div style={{ marginBottom: 16 }}>
          <p
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: 'var(--muted)',
              margin: '0 0 4px',
              textTransform: 'uppercase',
              letterSpacing: 0.06,
            }}
          >
            Top-line
          </p>
          <p style={{ margin: 0, fontSize: 14 }}>{topLine}</p>
        </div>
      )}

      {Array.isArray(goalHealth) && goalHealth.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <p
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: 'var(--muted)',
              margin: '0 0 6px',
              textTransform: 'uppercase',
              letterSpacing: 0.06,
            }}
          >
            Quarterly goal health
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {goalHealth.map((g, i) => {
              const status = resolveStatus(g?.status)
              return (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    padding: '8px 10px',
                    background: '#fff',
                    border: '1px solid var(--border)',
                    borderLeft: `3px solid ${status.color}`,
                    borderRadius: 6,
                  }}
                >
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: status.color,
                      flexShrink: 0,
                      marginTop: 6,
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: 8,
                        flexWrap: 'wrap',
                      }}
                    >
                      <span style={{ fontSize: 13, fontWeight: 700 }}>
                        {g?.pillarName || '—'}
                      </span>
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: status.text,
                          background: status.bg,
                          padding: '1px 7px',
                          borderRadius: 99,
                          letterSpacing: 0.03,
                        }}
                      >
                        {status.label}
                      </span>
                    </div>
                    {g?.summary && (
                      <p
                        style={{
                          margin: '2px 0 0',
                          fontSize: 12,
                          color: 'var(--text-secondary)',
                        }}
                      >
                        {g.summary}
                      </p>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {Array.isArray(highlights) && highlights.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <p
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: 'var(--muted)',
              margin: '0 0 6px',
              textTransform: 'uppercase',
              letterSpacing: 0.06,
            }}
          >
            This week's highlights
          </p>
          <ul style={{ margin: 0, paddingLeft: 22 }}>
            {highlights.map((h, i) => (
              <li key={i} style={{ marginBottom: 4 }}>
                {h}
              </li>
            ))}
          </ul>
        </div>
      )}

      {Array.isArray(needsAttention) && needsAttention.length > 0 && (
        <div
          style={{
            background: '#fffbeb',
            border: '1px solid #fde68a',
            borderRadius: 8,
            padding: '10px 14px',
          }}
        >
          <p
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: '#854d0e',
              margin: '0 0 6px',
              textTransform: 'uppercase',
              letterSpacing: 0.06,
            }}
          >
            Needs attention
          </p>
          <ul style={{ margin: 0, paddingLeft: 22 }}>
            {needsAttention.map((n, i) => (
              <li key={i} style={{ marginBottom: 4, color: '#78350f' }}>
                {n}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

/**
 * Render a Quarterly Pre-read as markdown text. Used by Copy as markdown
 * so Zoe can paste straight into her email.
 */
export function prereadToMarkdown(preread, dateLabel) {
  if (!preread) return ''
  const lines = []
  const dateStr = dateLabel || new Date().toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
  lines.push(`**Q2 weekly pre-read — ${dateStr}**`, '')
  if (preread.topLine) {
    lines.push('**Top-line**', preread.topLine, '')
  }
  if (Array.isArray(preread.goalHealth) && preread.goalHealth.length) {
    lines.push('**Quarterly goal health**')
    for (const g of preread.goalHealth) {
      const status = resolveStatus(g?.status)
      const dot = status.value === 'on-track'
        ? '🟢'
        : status.value === 'at-risk'
          ? '🟡'
          : status.value === 'off-track'
            ? '🔴'
            : status.value === 'done'
              ? '✅'
              : '⚪️'
      lines.push(`- ${dot} **${g?.pillarName || '—'}** — ${g?.summary || ''}`)
    }
    lines.push('')
  }
  if (Array.isArray(preread.highlights) && preread.highlights.length) {
    lines.push("**This week's highlights**")
    for (const h of preread.highlights) lines.push(`- ${h}`)
    lines.push('')
  }
  if (Array.isArray(preread.needsAttention) && preread.needsAttention.length) {
    lines.push('**Needs attention**')
    for (const n of preread.needsAttention) lines.push(`- ${n}`)
    lines.push('')
  }
  return lines.join('\n').trim()
}
